import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
// TODO: switch this to use named imports
// eslint-disable-next-line import/no-namespace
import * as ReactIntl from 'react-intl';
import { intlShape } from 'common/components/intl/intlShape';
import { gon } from 'common/gon';

export const {
  FormattedDate,
  FormattedTime,
  FormattedRelativeTime,
  FormattedNumber,
  FormattedPlural,
  FormattedMessage,
  FormattedHTMLMessage,
} = ReactIntl;

export const TRANSLATED_CLASS = 'intl-text';
export const MISSING_CLASS = 'translation-missing';
export const DATA_I18N_KEY = 'data-i18n-key';

/*
  In the test env, we intentionally render keys as values to increase
  stability in our tests and snapshots.
*/
export const translationIsMissing = (key, value) =>
  gon('env.RAILS_ENV') !== 'test' && key === value;

/*
 * In rails, each plural form has its own key, and we need to determine it
 * from the numeric value of <count>. A potential issue may arise since the algorithm
 * used here is not the same as in Rails.
 *
 * See:
 * http://guides.rubyonrails.org/i18n.html#pluralization.
 * https://github.com/yahoo/react-intl/wiki/API#formatplural
 * http://www.unicode.org/cldr/charts/latest/supplemental/language_plural_rules.html
 */
const getMessageId = (id, props, intl) =>
  props.values && props.values.count !== undefined
    ? `${id}.${intl.formatPlural(props.values.count, props)}`
    : id;

// Use this within other React components when rendering a translated message
export class TranslatedMessage extends React.Component {
  static contextType = ReactIntl.IntlContext;

  static propTypes = {
    id: PropTypes.string,
    values: PropTypes.object,
    textComponent: PropTypes.elementType,
  };

  getMessageId = () => getMessageId(this.props.id, this.props, this.context);

  renderMessageContent() {
    const parts = [].slice.call(arguments);
    const { props } = this;

    const message = parts[0];
    const messageId = this.getMessageId();
    const messageIdWithPrefix = `js.${messageId}`;

    const translationMissing = translationIsMissing(message, messageId);

    let className = classNames(classNames(TRANSLATED_CLASS, props.className), {
      [MISSING_CLASS]: translationMissing,
    });

    // TODO: react-intl now defaults this to React.Fragment. We reset it
    // back to our expected default of 'span', but in the future it would
    // be nice to use React.Fragments instead.
    // const { textComponent: Text } = this.context;
    const Text = props.textComponent || 'span';

    if (gon('env.RAILS_ENV') === 'production' && translationMissing) {
      className = `${className} invisibile`.trim();
      if (typeof bugsnagClient !== 'undefined') {
        bugsnagClient.notify(
          `TranslatedMessage: Missing translation id: ${messageId}`
        );
      }
    }

    return React.createElement(
      Text,
      { className, [DATA_I18N_KEY]: messageIdWithPrefix },
      ...parts
    );
  }

  render() {
    return (
      <ReactIntl.FormattedMessage {...this.props} id={this.getMessageId()}>
        {this.renderMessageContent.bind(this)}
      </ReactIntl.FormattedMessage>
    );
  }
}

const missingIntlFunction = () =>
  console.error(
    `Translation error: No react-intl formatting function was provided to translateMessages`
  );

export const messageGenerator = messages =>
  Object.values(messages).map(message =>
    typeof message === 'string' ? { id: message } : message
  );

/*
  message format:
    {
      name: identifying label for the translation, developer defined,
      id: react-intl, id corresponding to the flattened yml key,
      defaultMessage: react-intl, what will be displayed if no translation is found
        (if not provided, falls back to id)
      description: react-intl, context for the translator about how it's used
      values: react-intl, format for values to be inserted into the translation
        https://github.com/yahoo/react-intl/wiki/Components#formattedmessage
      value: react-intl, value for non-message translation, such as Date or Number
      options: react-intl, options that get passed to the various formatting
        functions and components for non-message components
    }
  output:
    {
      [name(defaults to id if name isn't provided)] : {
        raw: translated message/date/number as a raw string
        component: IntlComponent with the props from the message applied to it
        className: TRANSLATED_CLASS and, if missing a translation, MISSING_CLASS
      }
    }
*/

export const translateMessages = (messages = [], formatter) =>
  messages.reduce((acc, { name, ...message }) => {
    if (!name && !message.id) {
      console.error(
        `No name or id field was provided for translation: ${JSON.stringify(
          message
        )}`
      );
    }
    acc[name ? name : message.id] = formatter(message);

    return acc;
  }, {});

export const formatMessage = (
  intlFunc = missingIntlFunction,
  IntlComponent,
  intl
) => ({ values = {}, id, ...messageDescriptor }) => {
  const messageId = getMessageId(id, { values }, intl);
  const translatedMessage = intlFunc(
    { id: messageId, ...messageDescriptor },
    values
  );

  const translationMissing = translationIsMissing(translatedMessage, messageId);
  if (translationMissing && typeof bugsnagClient !== 'undefined') {
    bugsnagClient.notify(`formatMessage: Missing translation id: ${id}`);
  }

  const messageIdWithPrefix = `js.${id}`;
  let props = {
    id: messageId,
    ...messageDescriptor,
  };
  if (Object.keys(values).length > 0) {
    props = { ...props, values };
  }
  return {
    raw: translatedMessage,
    component: IntlComponent ? React.createElement(IntlComponent, props) : null,
    className: classNames(TRANSLATED_CLASS, {
      [MISSING_CLASS]: translationMissing,
    }),
    [DATA_I18N_KEY]: messageIdWithPrefix,
  };
};

export const formatNonMessage = (
  intlFunc = missingIntlFunction,
  IntlComponent
) => ({ value, ...options }) => ({
  raw: intlFunc(value, options),
  component: IntlComponent
    ? React.createElement(IntlComponent, { value, ...options })
    : null,
  className: TRANSLATED_CLASS,
});

const MessageRenderProp = ({ intl, messages, children }) =>
  children(
    translateMessages(
      messages,
      formatMessage(intl.formatMessage, TranslatedMessage, intl)
    ),
    intl
  );

/*
  Testing Caveat: to test, either do a full mount with an IntlProvider
    or use shallowWithIntl or mountWithIntl. If using shallow, to capture
    the output, you will have to call .dive() twice to get past the
    injectIntl HOC and the MessageRenderProp component. This applies to
    all of these IntlRenderProps
*/
export const IntlMessageRenderProp = ReactIntl.injectIntl(MessageRenderProp);

const DateRenderProp = ({ intl, dates, children }) =>
  children(
    translateMessages(dates, formatNonMessage(intl.formatDate, FormattedDate)),
    intl
  );

export const IntlDateRenderProp = ReactIntl.injectIntl(DateRenderProp);

const NumberRenderProp = ({ intl, numbers, children }) =>
  children(
    translateMessages(
      numbers,
      formatNonMessage(intl.formatNumber, FormattedNumber)
    ),
    intl
  );

export const IntlNumberRenderProp = ReactIntl.injectIntl(NumberRenderProp);

// Use this when you need to get just the translated string. Must provide intl.
export const translateMessage = (intl, id, values) =>
  intl.formatMessage({ id: getMessageId(id, { values }, intl) }, values);

// Curried version of translateMessage.
export const messageTranslator = intl => (id, values) =>
  translateMessage(intl, id, values);

export class FormattedPercent extends React.Component {
  static propTypes = {
    value: PropTypes.string,
  };

  render() {
    if (!this.props.value) {
      return null;
    }

    const value = Number(this.props.value.replace('%', ''));
    if (value === 0) {
      return null;
    }

    return (
      <FormattedNumber
        value={value / 100}
        style="percent"
        minimumFractionDigits={0}
        maximumFractionDigits={1}
      />
    );
  }
}

export const IntlContext = React.createContext({});

const onReactIntlError = error => {
  if (typeof bugsnagClient !== 'undefined') {
    bugsnagClient.notify(error);
  }
};

export const IntlProvider = ({ locale, messages, children }) => {
  const { locale: contextLocale, messages: contextMessages } = useContext(
    IntlContext
  );
  const { locale: globalLocale, messages: globalMessages } =
    global.translations || {};

  return (
    <ReactIntl.IntlProvider
      locale={locale || contextLocale || globalLocale}
      messages={messages || contextMessages || globalMessages}
      onError={onReactIntlError}
      textComponent="span"
    >
      <InjectedLegacyContextIntlProviderAdapter>
        {children}
      </InjectedLegacyContextIntlProviderAdapter>
    </ReactIntl.IntlProvider>
  );
};

// This is a Context Provider that takes its intl prop and passes it down via the legacy
// React Context API. We use react-intl's injectIntl function to inject the intl object
// from the new Reaect Context API to this component's intl prop.
// So we essentially convert the new React Context API to the legacy Context API for
// backwards compatibility in our components.
class LegacyContextIntlProviderAdapter extends React.Component {
  static childContextTypes = {
    intl: intlShape,
  };

  getChildContext() {
    return { intl: this.props.intl };
  }

  render() {
    return this.props.children;
  }
}

LegacyContextIntlProviderAdapter.propTypes = { intl: intlShape.isRequired };

const InjectedLegacyContextIntlProviderAdapter = ReactIntl.injectIntl(
  LegacyContextIntlProviderAdapter
);

// A simple wrapper around IntlProvider, where the translations and locale
// are provided by the new React Context API. This allows us to decouple our
// IntlProvider from global.translations.
//
// We add a "currency" prop along with the standard "locale" and "translations"
// props that react-intl accepts. Since currency is also related to I18n, it
// makes sense to include it here. The currency props are used by our PriceSpan
// component.
export const IntlContextProvider = ({
  currency,
  children,
  locale,
  messages,
}) => (
  <IntlContext.Provider value={{ currency, locale, messages }}>
    <IntlProvider>{children}</IntlProvider>
  </IntlContext.Provider>
);

IntlContextProvider.propTypes = {
  children: PropTypes.node,
  currency: PropTypes.shape({
    isoCode: PropTypes.string,
    subUnitToUnit: PropTypes.number,
    symbol: PropTypes.string,
  }),
  locale: PropTypes.string,
  messages: PropTypes.objectOf(PropTypes.string),
};

export const TranslatedLinkMessage = ({
  id,
  link,
  linkText,
  inlineData,
  opts = {},
}) => {
  const intl = ReactIntl.useIntl();
  const templateKey = opts.templateKey || 'anchor';
  const linkData = translateMessage(intl, link);
  const linkTextData = translateMessage(intl, linkText);
  const anchor = <a href={linkData}>{linkTextData}</a>;
  return (
    <TranslatedMessage
      id={id}
      values={{ [templateKey]: anchor, ...inlineData }}
    />
  );
};
